import React, { useState } from "react";
import { Upload, FileText, Trash2, Loader2, Replace, X, Download } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useLanguage } from "../../context/LanguageContext";
import { api, API_BASE } from "../../lib/api";
import { toast } from "sonner";
import BiField from "./BiField";

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Force a download (rather than inline render) by fetching the file with
// credentials and triggering a Blob download. Surfaces the backend's specific
// 404/403/500 detail rather than the generic toast, since responseType=blob
// otherwise hides the JSON error body inside the Blob.
const downloadFileById = async (fileId, filename, t) => {
  try {
    const { data } = await api.get(`/files/${fileId}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    const status = e?.response?.status;
    // axios with responseType=blob stores the error body as a Blob — read it
    // so we can show the actual detail string from FastAPI.
    let detail = "";
    const body = e?.response?.data;
    if (body && typeof body.text === "function") {
      try {
        const txt = await body.text();
        try { detail = JSON.parse(txt).detail || ""; } catch { detail = txt; }
      } catch { /* ignore */ }
    } else if (typeof body === "string") {
      detail = body;
    }
    let msg;
    if (status === 404) {
      msg = t("err_file_not_found") + (detail ? ` (${detail})` : "");
    } else if (status === 403) {
      msg = t("err_file_forbidden");
    } else if (status >= 500) {
      msg = t("err_server") + (detail ? ` — ${detail}` : "");
    } else {
      msg = detail || t("err_generic");
    }
    // console.error logs the full picture for next-time diagnosis.
    // eslint-disable-next-line no-console
    console.error("file-download failed", { fileId, filename, status, detail, error: e });
    toast.error(msg);
  }
};

const ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_BYTES = 10 * 1024 * 1024;

const validateFile = (file, t) => {
  const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
  if (!ALLOWED.includes(file.type) && !ALLOWED_EXT.includes(ext)) {
    return t("err_file_type");
  }
  if (file.size > MAX_BYTES) {
    return t("err_file_too_large");
  }
  return null;
};

const FileSlot = ({
  slot,
  en,
  zh,
  required,
  caseId,
  ensureCase,
  fileRef,
  onChange,
  disabled,
  error,
}) => {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const inputId = `file-${slot}`;

  const onPick = async (file) => {
    if (!file) return;
    const err = validateFile(file, t);
    if (err) {
      toast.error(err);
      return;
    }
    setUploading(true);
    try {
      const id = caseId || (ensureCase && (await ensureCase()));
      if (!id) throw new Error("no case id");
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/cases/${id}/files/${slot}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
      toast.success(t("toast_file_uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async () => {
    if (!caseId || !fileRef) return;
    try {
      await api.delete(`/cases/${caseId}/files/${slot}`);
      onChange(null);
      toast.success(t("toast_file_removed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    }
  };

  return (
    <BiField id={inputId} en={en} zh={zh} required={required} error={error}>
      <div className="border border-dashed border-slate-300 rounded-md bg-slate-50/40">
        {fileRef ? (
          <div className="flex items-center gap-3 px-4 py-3" data-testid={`file-slot-${slot}-filled`}>
            <div className="h-9 w-9 rounded-md bg-white border border-slate-200 flex items-center justify-center">
              <FileText className="h-4 w-4 text-[#0A2540]" />
            </div>
            <div className="flex-1 min-w-0">
              <a
                href={`${API_BASE}/files/${fileRef.file_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-[#0A2540] truncate block hover:text-[#007AFF]"
                data-testid={`file-slot-${slot}-link`}
              >
                {fileRef.filename}
              </a>
              <p className="text-xs text-slate-500">{fmtSize(fileRef.size)}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-[#0A2540]"
              onClick={() => downloadFileById(fileRef.file_id, fileRef.filename, t)}
              data-testid={`file-slot-${slot}-download`}
              title={t("download_file")}
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline ml-1 text-xs">{t("download_file")}</span>
            </Button>
            {!disabled ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-[#0A2540]"
                  onClick={() => document.getElementById(inputId)?.click()}
                  disabled={uploading}
                  data-testid={`file-slot-${slot}-replace`}
                >
                  <Replace className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-rose-600 hover:bg-rose-50"
                  onClick={onRemove}
                  disabled={uploading}
                  data-testid={`file-slot-${slot}-remove`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => document.getElementById(inputId)?.click()}
            className="w-full px-4 py-5 flex flex-col items-center gap-2 text-slate-500 hover:bg-slate-50 transition-colors focus-ring rounded-md"
            data-testid={`file-slot-${slot}-empty`}
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Upload className="h-5 w-5 text-slate-400" />
            )}
            <span className="text-sm">
              {uploading ? t("case_form_uploading") : t("case_form_drop_or_click")}
            </span>
          </button>
        )}
        <input
          id={inputId}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) onPick(f);
          }}
          disabled={disabled || uploading}
        />
      </div>
    </BiField>
  );
};

const OtherDocuments = ({ caseId, ensureCase, items, onChange, disabled, error }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setDescription("");
    setFile(null);
  };

  const onPick = (f) => {
    if (!f) {
      setFile(null);
      return;
    }
    const err = validateFile(f, t);
    if (err) {
      toast.error(err);
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file || !description.trim()) return;
    setUploading(true);
    try {
      const id = caseId || (ensureCase && (await ensureCase()));
      if (!id) throw new Error("no case id");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", description.trim());
      const { data } = await api.post(`/cases/${id}/files/other`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...(items || []), data]);
      toast.success(t("toast_file_uploaded"));
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async (file_id) => {
    try {
      await api.delete(`/cases/${caseId}/files/other/${file_id}`);
      onChange((items || []).filter((it) => it.file.file_id !== file_id));
      toast.success(t("toast_file_removed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    }
  };

  return (
    <BiField en="Other Documents" zh="其他文件" error={error}>
      <div className="space-y-2">
        {(items || []).length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-md px-4 py-3 text-sm text-slate-500">
            {t("case_form_no_file")}
          </div>
        ) : (
          (items || []).map((it) => (
            <div
              key={it.file.file_id}
              className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-md"
              data-testid={`other-doc-${it.file.file_id}`}
            >
              <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center">
                <FileText className="h-4 w-4 text-[#0A2540]" />
              </div>
              <div className="flex-1 min-w-0">
                <a
                  href={`${API_BASE}/files/${it.file.file_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-[#0A2540] truncate block hover:text-[#007AFF]"
                >
                  {it.file.filename}
                </a>
                <p className="text-xs text-slate-500 truncate">{it.description}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-slate-600 hover:text-[#0A2540]"
                onClick={() => downloadFileById(it.file.file_id, it.file.filename, t)}
                data-testid={`other-doc-download-${it.file.file_id}`}
                title={t("download_file")}
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline ml-1 text-xs">{t("download_file")}</span>
              </Button>
              {!disabled ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-rose-600 hover:bg-rose-50"
                  onClick={() => onRemove(it.file.file_id)}
                  data-testid={`other-doc-remove-${it.file.file_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          ))
        )}
        {!disabled ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(true)}
            className="border-dashed"
            data-testid="add-other-doc-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            {t("case_form_add_other")}
          </Button>
        ) : null}
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="sm:max-w-md" data-testid="other-doc-dialog">
          <DialogHeader>
            <DialogTitle>{t("case_form_other_dialog_title")}</DialogTitle>
            <DialogDescription>{t("case_form_other_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <BiField id="other-desc" en="Description" zh="描述" required>
              <Input
                id="other-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("case_form_other_description_placeholder")}
                required
                data-testid="other-doc-description"
              />
            </BiField>
            <div className="space-y-1.5">
              <Label htmlFor="other-file" className="text-sm font-medium">
                {t("case_form_other_pick_file")}
              </Label>
              <Input
                id="other-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={(e) => onPick(e.target.files?.[0] || null)}
                data-testid="other-doc-file"
              />
              {file ? (
                <p className="text-xs text-slate-500">
                  {file.name} · {fmtSize(file.size)}
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setOpen(false); reset(); }}
              >
                {t("modal_cancel")}
              </Button>
              <Button
                type="submit"
                disabled={!file || !description.trim() || uploading}
                className="bg-[#0A2540] hover:bg-[#051626] text-white"
                data-testid="other-doc-upload-submit"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("case_form_uploading")}
                  </>
                ) : (
                  t("case_form_upload")
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </BiField>
  );
};

export { FileSlot, OtherDocuments };
